
import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Home, Receipt, BarChart2, MoreHorizontal, FileText, Settings, Building, ClipboardList, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export function BottomNavigation() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  console.log("🔍 DEBUG (BottomNavigation): rendering");
  console.log("🔍 DEBUG (BottomNavigation): Current organizationId from params:", organizationId);
  console.log("🔍 DEBUG (BottomNavigation): Current location pathname:", location.pathname);
  console.log("🔍 DEBUG (BottomNavigation): Is mobile:", isMobile);
  
  // Query to get the default organization if no organization ID is provided
  const { data: defaultOrgId, isLoading } = useQuery({
    queryKey: ["default-organization-nav"],
    queryFn: async () => {
      console.log("🔍 DEBUG (BottomNavigation): Fetching default organization for bottom nav. User ID:", userId);
      if (!userId) return null;
      
      try {
        const { data, error } = await supabase
          .from("organization_members")
          .select("organization_id")
          .eq("user_id", userId)
          .eq("is_default", true)
          .maybeSingle();
        
        if (error) {
          console.error("🔴 ERROR (BottomNavigation): Error fetching default organization:", error);
          return null;
        }
        
        if (!data) {
          console.log("🔍 DEBUG (BottomNavigation): No default organization found for bottom navigation");
          const { data: anyOrg, error: anyOrgError } = await supabase
            .from("organization_members")
            .select("organization_id")
            .eq("user_id", userId)
            .limit(1);
            
          if (anyOrgError || !anyOrg || anyOrg.length === 0) {
            console.log("🔍 DEBUG (BottomNavigation): No organizations found for user");
            return null;
          }
          
          console.log("🔍 DEBUG (BottomNavigation): Using first organization as fallback:", anyOrg[0].organization_id);
          return anyOrg[0].organization_id;
        }
        
        console.log("🔍 DEBUG (BottomNavigation): Found default organization for nav:", data.organization_id);
        return data.organization_id;
      } catch (error) {
        console.error("🔴 ERROR (BottomNavigation): Exception in fetchDefaultOrg:", error);
        return null;
      }
    },
    enabled: !organizationId && !!userId,
  });

  useEffect(() => {
    // Get current user ID
    const getUser = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        console.log("🔍 DEBUG (BottomNavigation): Current user:", data.user?.id);
        setUserId(data.user?.id || null);
      } catch (error) {
        console.error("🔴 ERROR (BottomNavigation): Error getting user:", error);
      }
    };
    getUser();
  }, []);
  
  // Don't render on non-mobile devices
  if (!isMobile) {
    console.log("🔍 DEBUG (BottomNavigation): Not rendering - not on mobile");
    return null;
  }

  // Don't render if the user is not logged in or on auth pages
  if (location.pathname === '/login' || location.pathname === '/') {
    console.log("🔍 DEBUG (BottomNavigation): Not rendering - on auth page");
    return null;
  }

  // Use organizationId from params or the default organization ID from the query
  const activeOrgId = organizationId || defaultOrgId;
  console.log("🔍 DEBUG (BottomNavigation): Active organization ID for navigation:", activeOrgId);

  // If there's no active org and we're still loading, don't render yet to avoid
  // incorrect navigation
  if (!activeOrgId && isLoading) {
    console.log("🔍 DEBUG (BottomNavigation): Not rendering - loading default org");
    return null;
  }

  // If there's no active org and we're done loading, show a placeholder
  const orgPath = activeOrgId ? `/app/${activeOrgId}` : "/app/organizations";
  console.log("🔍 DEBUG (BottomNavigation): Organization path for navigation:", orgPath);

  const tabs = [
    {
      icon: Home,
      label: "Home",
      path: activeOrgId ? `${orgPath}/dashboard` : "/app/organizations",
      active: location.pathname.includes("/dashboard") || location.pathname === `${orgPath}`,
    },
    {
      icon: Receipt,
      label: "Expenses",
      path: activeOrgId ? `${orgPath}/expenses` : "/app/organizations",
      active: location.pathname.includes("/expenses") && !location.pathname.includes("/expense-reports"),
    },
    {
      // This is the add button which is rendered separately in the middle
      icon: Plus,
      label: "Add",
      path: activeOrgId ? `${orgPath}/add-expense` : "/app/organizations",
      isMiddleAction: true,
      active: location.pathname.includes("/add-expense"),
    },
    {
      icon: BarChart2,
      label: "Analytics",
      path: activeOrgId ? `${orgPath}/analytics` : "/app/organizations",
      active: location.pathname.includes("/analytics"),
    },
    {
      icon: MoreHorizontal,
      label: "More",
      path: "#", // Will open the drawer menu
      active: location.pathname.includes("/reports") || 
              location.pathname.includes("/expense-reports") || 
              location.pathname.includes("/settings"),
      onClick: () => setIsOpen(true),
    },
  ];

  const handleTabClick = (tab: typeof tabs[0]) => {
    console.log("🔍 DEBUG (BottomNavigation): Tab clicked:", tab.label);
    if (tab.onClick) {
      tab.onClick();
      return;
    }
    
    if (tab.isMiddleAction) {
      console.log("🔍 DEBUG (BottomNavigation): Navigating to add expense page:", tab.path);
    }
    
    navigate(tab.path);
  };

  // Additional menu items for the "More" sheet
  const moreMenuItems = [
    { 
      icon: FileText,
      label: "Reports", 
      path: activeOrgId ? `${orgPath}/reports` : "/app/organizations",
      active: location.pathname.includes("/reports")
    },
    { 
      icon: ClipboardList, 
      label: "Expense Reports", 
      path: activeOrgId ? `${orgPath}/expense-reports` : "/app/organizations",
      active: location.pathname.includes("/expense-reports")
    },
    { 
      icon: Settings,
      label: "Settings", 
      path: activeOrgId ? `${orgPath}/settings` : "/app/organizations",
      active: location.pathname.includes("/settings")
    },
    {
      icon: Building,
      label: "Organizations",
      path: "/app/organizations",
      active: location.pathname.includes("/organizations")
    }
  ];

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg md:hidden">
        <nav className="flex items-center justify-around h-16 relative">
          {tabs.map((tab) => {
            // For the middle action button, render the Add button
            if (tab.isMiddleAction) {
              return (
                <div key={tab.label} className="flex flex-1 items-center justify-center">
                  <Button
                    variant="default"
                    size="icon"
                    className="add-expense-button h-12 w-12 rounded-full flex items-center justify-center"
                    onClick={() => handleTabClick(tab)}
                  >
                    <Plus className="h-6 w-6" />
                  </Button>
                </div>
              );
            }
            
            return (
              <Button
                key={tab.label}
                variant="ghost"
                size="icon"
                className={cn(
                  "flex flex-1 flex-col items-center justify-center rounded-none h-full gap-1",
                  tab.active ? "text-primary" : "text-gray-500"
                )}
                onClick={() => handleTabClick(tab)}
              >
                {tab.icon && <tab.icon className="h-5 w-5" />}
                <span className="text-[10px]">{tab.label}</span>
              </Button>
            );
          })}
        </nav>
      </div>

      {/* More menu sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="bottom" className="ios-sheet">
          <SheetHeader>
            <SheetTitle>More Options</SheetTitle>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            {moreMenuItems.map((item) => (
              <Button
                key={item.label}
                variant={item.active ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => {
                  console.log("🔍 DEBUG (BottomNavigation): More menu item clicked:", item.label, "navigating to:", item.path);
                  navigate(item.path);
                  setIsOpen(false);
                }}
              >
                {item.icon && <item.icon className="h-5 w-5 mr-2" />}
                {item.label}
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
